/**
 * ユーザー画面（/admin/users）。
 *
 * 仕様書: docs/01_spec/spec_user_management.md
 *
 * 機能:
 *   - 全 admin（pending スタブ含む）の一覧表示
 *   - 招待モーダル（氏名 + Gmail + 部署 + 権限レベル + owner フラグ）
 *   - ステータスバッジ（手続中 / アクティブ）+ 部署 / レベル バッジ
 *   - 編集モーダル（氏名 / 部署 / レベル / owner）
 *   - 削除ボタン（deleteAdmin Function 経由：active = admins doc 削除、pending = invitation revoke + スタブ削除）
 *   - pending には「再送」ボタン
 *
 * v3.2.16 (2026-05-14): 「初期化」ボタンはロール設定タブ (settings > ロール設定) に移設
 */

import { LEVELS, LEVEL_LABELS, type DepartmentId, type Level } from '@goenhub/shared';
import { httpsCallable } from 'firebase/functions';
import { ExternalLink, Mail, Pencil, Send, UserPlus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useDepartments, useMyPermissions } from '@/features/permissions';
import {
  useAdmins,
  useDeleteAdmin,
  useResendInvitation,
  useUpdateAdminInfo,
  type AdminRecord,
} from '@/hooks/useAdmins';
import { useAuth } from '@/hooks/useAuth';
import { functions } from '@/lib/firebase';
import { paths } from '@/routes/paths';

interface InviteAdminRequest {
  email: string;
  dept: DepartmentId;
  level: Level;
  isOwner: boolean;
  fullName?: string;
}

interface InviteAdminResponse {
  ok: boolean;
  invitationId: string;
  emailStatus?: 'sent' | 'failed';
  emailError?: string | null;
}

const inviteAdminFn = httpsCallable<InviteAdminRequest, InviteAdminResponse>(
  functions,
  'inviteAdmin',
);

export function UsersPage() {
  const { user } = useAuth();
  const { isOwner } = useMyPermissions();
  const { data: admins, isLoading } = useAdmins();
  const { data: departments } = useDepartments();
  const updateInfo = useUpdateAdminInfo();
  const deleteMutation = useDeleteAdmin();
  const resendMutation = useResendInvitation();

  // 招待モーダル
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteDept, setInviteDept] = useState<DepartmentId>('admin');
  const [inviteLevel, setInviteLevel] = useState<Level>('employee');
  const [inviteIsOwner, setInviteIsOwner] = useState(false);
  const [inviting, setInviting] = useState(false);

  // 編集モーダル
  const [editTarget, setEditTarget] = useState<AdminRecord | null>(null);
  const [editFullName, setEditFullName] = useState('');
  const [editDept, setEditDept] = useState<DepartmentId>('admin');
  const [editLevel, setEditLevel] = useState<Level>('employee');
  const [editIsOwner, setEditIsOwner] = useState(false);

  useEffect(() => {
    if (!editTarget) return;
    setEditFullName(editTarget.fullName ?? editTarget.displayName ?? '');
    setEditDept(((editTarget.dept as DepartmentId | undefined) ?? 'admin') as DepartmentId);
    setEditLevel(((editTarget.level as Level | undefined) ?? 'employee') as Level);
    setEditIsOwner(Boolean(editTarget.isOwner));
  }, [editTarget]);

  const handleInvite = async () => {
    if (!inviteEmail || !inviteDept || !inviteLevel) return;
    setInviting(true);
    try {
      const res = await inviteAdminFn({
        email: inviteEmail,
        dept: inviteDept,
        level: inviteLevel,
        isOwner: inviteIsOwner,
        fullName: inviteFullName || undefined,
      });
      const emailStatus = res.data.emailStatus;
      if (emailStatus === 'sent') {
        toast.success(`${inviteEmail} に招待メールを送信しました`);
      } else if (emailStatus === 'failed') {
        toast.error(
          `招待は作成されましたがメール送信に失敗: ${res.data.emailError ?? ''}（一覧から再送できます）`,
        );
      } else {
        toast.success(`${inviteEmail} に招待を作成しました`);
      }
      setInviteOpen(false);
      setInviteEmail('');
      setInviteFullName('');
      setInviteDept('admin');
      setInviteLevel('employee');
      setInviteIsOwner(false);
    } catch (err) {
      toast.error(`招待に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setInviting(false);
    }
  };

  const handleDelete = async (admin: AdminRecord) => {
    if (admin.uid === user?.uid) {
      toast.error('自分自身は削除できません');
      return;
    }
    const verb = admin.resolvedStatus === 'pending' ? '招待を取り消し' : '削除';
    if (!window.confirm(`${admin.email} を${verb}します。よろしいですか？`)) return;
    try {
      await deleteMutation.mutateAsync(admin.uid);
      toast.success(`${admin.email} を${verb}しました`);
    } catch (err) {
      toast.error(`${verb}に失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleResend = async (admin: AdminRecord) => {
    const invId = (admin as unknown as { invitationId?: string }).invitationId;
    if (!invId) {
      toast.error('招待 ID が見つかりません');
      return;
    }
    try {
      const result = (await resendMutation.mutateAsync(invId)) as {
        emailStatus: 'sent' | 'failed';
        emailError: string | null;
      };
      if (result.emailStatus === 'sent') {
        toast.success(`${admin.email} に招待メールを再送しました（有効期限を延長）`);
      } else {
        toast.error(`メール送信に失敗: ${result.emailError ?? ''}`);
      }
    } catch (err) {
      toast.error(`再送に失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleEditSubmit = async () => {
    if (!editTarget) return;
    try {
      await updateInfo.mutateAsync({
        adminId: editTarget.uid,
        fullName: editFullName.trim() || null,
        dept: editDept,
        level: editLevel,
        isOwner: editIsOwner,
      });
      toast.success('ユーザー情報を更新しました');
      setEditTarget(null);
    } catch (err) {
      toast.error(`更新に失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;

  const deptOptions =
    departments && departments.length > 0
      ? departments
      : [
          {
            id: 'admin' as const,
            name: '管理者',
            isSystem: true,
            order: 0,
            createdAt: '',
            updatedAt: '',
          },
          {
            id: 'sales' as const,
            name: '営業',
            isSystem: true,
            order: 1,
            createdAt: '',
            updatedAt: '',
          },
          {
            id: 'marketing' as const,
            name: 'マーケティング',
            isSystem: true,
            order: 2,
            createdAt: '',
            updatedAt: '',
          },
          {
            id: 'accounting' as const,
            name: '経理',
            isSystem: true,
            order: 3,
            createdAt: '',
            updatedAt: '',
          },
        ];

  const deptLabel = (id: DepartmentId) => deptOptions.find((d) => d.id === id)?.name ?? id;

  return (
    <Screen
      title="ユーザー"
      description="管理者アカウントの招待・編集・削除を行います"
      actions={
        isOwner ? (
          <Button type="button" variant="primary" size="sm" onClick={() => setInviteOpen(true)}>
            <UserPlus />
            新規ユーザー招待
          </Button>
        ) : null
      }
    >
      <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
        {admins?.length === 0 ? (
          <div className="p-6 text-center text-[var(--text-muted)] text-sm">
            ユーザーが登録されていません
          </div>
        ) : (
          admins?.map((admin) => {
            const isSelf = admin.uid === user?.uid;
            const pending = admin.resolvedStatus === 'pending';
            const adminDept: DepartmentId = (admin.dept ?? 'admin') as DepartmentId;
            const adminLevel: Level = (admin.level ?? 'employee') as Level;
            const adminIsOwner: boolean = Boolean(admin.isOwner);

            return (
              <div
                key={admin.uid}
                className="flex items-center gap-3 p-3 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
              >
                {admin.photoUrl ? (
                  <img src={admin.photoUrl} alt="" className="size-8 rounded-full flex-shrink-0" />
                ) : (
                  <div className="size-8 rounded-full bg-[var(--bg-hover)] flex items-center justify-center text-xs flex-shrink-0">
                    {(admin.fullName || admin.displayName || admin.email).charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">
                    {pending ? (
                      <span>{admin.fullName || admin.displayName || admin.email}</span>
                    ) : (
                      <Link
                        to={paths.adminUserDetail.replace(':uid', admin.uid)}
                        className="hover:text-[var(--accent)] hover:underline"
                      >
                        {admin.fullName || admin.displayName || admin.email}
                      </Link>
                    )}
                    {isSelf && (
                      <span className="text-[var(--text-muted)] text-xs ml-2">(自分)</span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--text-muted)] truncate">{admin.email}</div>
                </div>

                <div className="flex items-center gap-2">
                  {pending ? (
                    <Badge variant="draft">手続中</Badge>
                  ) : (
                    <Badge variant="approved">アクティブ</Badge>
                  )}
                  <Badge>{deptLabel(adminDept)}</Badge>
                  <Badge variant={adminIsOwner ? 'accent' : 'default'}>
                    {adminIsOwner ? 'owner' : LEVEL_LABELS[adminLevel]}
                  </Badge>

                  {/* T7 / v3.2.11: 詳細画面へ遷移するアイコン (active のみ) */}
                  {!pending && (
                    <Link
                      to={paths.adminUserDetail.replace(':uid', admin.uid)}
                      className="inline-flex items-center justify-center size-8 rounded text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-elevated)] transition-colors"
                      title="ユーザー詳細を開く"
                    >
                      <ExternalLink className="size-4" />
                    </Link>
                  )}

                  {isOwner && !isSelf && (
                    <>
                      {pending && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleResend(admin)}
                          disabled={resendMutation.isPending}
                          title="招待メール再送"
                        >
                          <Send />
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditTarget(admin)}
                        title="編集"
                      >
                        <Pencil />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(admin)}
                        title={pending ? '招待を取り消し' : '削除'}
                      >
                        <X />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 招待モーダル */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新規ユーザー招待</DialogTitle>
            <DialogDescription>
              @caudesign.jp または @gmail.com のアドレスを指定して招待します。被招待者は 7 日以内に
              Google SSO でサインインしてください。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="invite-name">氏名</Label>
              <Input
                id="invite-name"
                placeholder="山田 太郎"
                value={inviteFullName}
                onChange={(e) => setInviteFullName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="invite-email">メールアドレス</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="name@caudesign.jp"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>部署</Label>
                <Select value={inviteDept} onValueChange={(v) => setInviteDept(v as DepartmentId)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {deptOptions.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>権限レベル</Label>
                <Select value={inviteLevel} onValueChange={(v) => setInviteLevel(v as Level)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((lv) => (
                      <SelectItem key={lv} value={lv}>
                        {LEVEL_LABELS[lv]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={inviteIsOwner}
                onChange={(e) => setInviteIsOwner(e.target.checked)}
                className="size-4"
              />
              <span>owner 権限を付与（全機能アクセス可能）</span>
            </label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setInviteOpen(false)}
              disabled={inviting}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleInvite}
              disabled={inviting || !inviteEmail}
            >
              <Mail />
              {inviting ? '送信中...' : '招待を送信'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 編集モーダル */}
      <Dialog open={Boolean(editTarget)} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ユーザー編集</DialogTitle>
            <DialogDescription>
              {editTarget?.email}
              {editTarget?.resolvedStatus === 'pending' && (
                <span className="ml-2 text-[var(--accent-amber)]">（手続中）</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">氏名</Label>
              <Input
                id="edit-name"
                value={editFullName}
                onChange={(e) => setEditFullName(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>部署</Label>
                <Select value={editDept} onValueChange={(v) => setEditDept(v as DepartmentId)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {deptOptions.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>権限レベル</Label>
                <Select value={editLevel} onValueChange={(v) => setEditLevel(v as Level)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((lv) => (
                      <SelectItem key={lv} value={lv}>
                        {LEVEL_LABELS[lv]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={editIsOwner}
                onChange={(e) => setEditIsOwner(e.target.checked)}
                className="size-4"
              />
              <span>owner 権限を付与（全機能アクセス可能）</span>
            </label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditTarget(null)}
              disabled={updateInfo.isPending}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleEditSubmit}
              disabled={updateInfo.isPending}
            >
              {updateInfo.isPending ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Screen>
  );
}
